/**
 * System URI prefixes — these are stripped for clean display.
 * App/custom prefixes are kept to show provenance.
 */
const SYSTEM_PREFIXES = new Set([
  'paradigm', 'raw', 'context', 'golden', 'self', 'ontology', 'financial',
  'content', 'education', 'experience', 'finance', 'health', 'identity',
  'legal', 'lifestyle', 'planning', 'work', 'system',
]);

function titleCase(str) {
  return str
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Derive a human-friendly label from a schema name.
 *
 * System schemas (paradigm, raw, golden, etc.) drop the prefix:
 *   "goal" → "Goal", "health-metric" → "Health Metric"
 *
 * App/custom schemas keep the prefix to show provenance:
 *   "missions:weekly-review" → "missions: Weekly Review"
 */
export function schemaLabel(name) {
  if (!name) return '';
  const colon = name.indexOf(':');
  if (colon === -1) return titleCase(name);
  const prefix = name.slice(0, colon);
  const suffix = name.slice(colon + 1);
  if (SYSTEM_PREFIXES.has(prefix)) return titleCase(suffix);
  return `${prefix}: ${titleCase(suffix)}`;
}

/**
 * Resolve a schema by id against a list that may only carry the latest version
 * of each schema_name — the shape `/schemas?group_by_uri=true` returns. (The
 * param is named for a `uri` field that no longer exists; it groups by
 * schema_name.)
 *
 * Permissions pin one specific version id. Once a schema has been versioned
 * that id belongs to an older version, which is absent from the grouped list —
 * and nothing else in the response points back to it, so `find(s => s.id === id)`
 * misses and the caller renders a raw UUID. `version_ids` carries the whole
 * family, which is what lets an older id resolve to the current schema.
 */
export function findSchemaById(allSchemas, id) {
  if (!id) return null;
  const key = String(id);
  const list = allSchemas || [];
  return (
    list.find(s => String(s.id) === key) ||
    list.find(s => (s.version_ids || []).some(v => String(v) === key)) ||
    null
  );
}

/**
 * Given a set of selected schema IDs and the full list of known schemas,
 * return a Set that includes the selected IDs plus all descendant schema IDs.
 *
 * @param {string[]} selectedIds  - Array of selected schema UUIDs
 * @param {Object[]} allSchemas   - Full schema list, each with { id, extends? }
 * @returns {Set<string>}
 */
export function expandSchemaIds(selectedIds, allSchemas) {
  const expanded = new Set(selectedIds.map(String));
  let frontier = new Set(selectedIds.map(String));

  while (frontier.size > 0) {
    const next = new Set();
    for (const schema of allSchemas) {
      if (schema.extends && frontier.has(String(schema.extends)) && !expanded.has(String(schema.id))) {
        next.add(String(schema.id));
      }
    }
    if (next.size === 0) break;
    next.forEach(id => expanded.add(id));
    frontier = next;
  }

  return expanded;
}
